import { __ } from "@wordpress/i18n";
import { 
  useBlockProps, 
  InnerBlocks, 
  MediaUpload, 
  MediaUploadCheck
} from "@wordpress/block-editor";
import { Button } from "@wordpress/components";

export default function Edit({ attributes, setAttributes, isSelected }) {
  const { title, imageUrl, imageId, linkText, linkUrl, showLink } = attributes;
  const blockProps = useBlockProps({
    className: "tab-item-block"
  });

  return (
    <div {...blockProps}>
      <div className="tab-item-header mb-4">
        <input
          type="text"
          value={title}
          onChange={(e) => setAttributes({ title: e.target.value })}
          placeholder={__('Tab Title', 'puroclean')}
          className="w-full text-xl font-semibold mb-2 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      <div className="tab-item-content flex gap-6">
        {/* Content Section */}
        <div className="flex-1">
          {/* InnerBlocks for rich content - isolated per tab */}
          <div className="border border-gray-200 rounded-lg p-4 min-h-[150px] bg-white mb-6">
            <InnerBlocks
              allowedBlocks={[
                'core/paragraph',
                'core/heading',
                'core/list',
                'core/quote',
                'core/separator',
                'core/spacer',
                'core/image',
                'core/columns',
                'core/group',
              ]}
              template={[
                ['core/paragraph', { 
                  placeholder: __('Start writing your tab content...', 'puroclean'),
                  className: 'text-gray-700'
                }]
              ]}
              templateLock={false}
              renderAppender={InnerBlocks.ButtonBlockAppender}
            />
          </div>

          {/* Link Section - Always visible */}
          <div className="border-t pt-4">
            <div className="flex items-center gap-4 mb-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={showLink}
                  onChange={(e) => setAttributes({ showLink: e.target.checked })}
                />
                <span className="text-sm font-medium">{__('Show Link', 'puroclean')}</span>
              </label>
            </div>
            
            {showLink && (
              <div className="space-y-3">
                <input
                  type="text"
                  value={linkText}
                  onChange={(e) => setAttributes({ linkText: e.target.value })}
                  placeholder={__('Link Text', 'puroclean')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
                <input
                  type="text"
                  value={linkUrl}
                  onChange={(e) => setAttributes({ linkUrl: e.target.value })}
                  placeholder={__('Link URL', 'puroclean')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
              </div>
            )}
          </div>

          {/* Link Preview */}
          {showLink && linkText && linkUrl && (
            <div className="mt-4">
              <a
                href="#"
                className="inline-block bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors duration-200 font-semibold"
                onClick={(e) => e.preventDefault()}
              >
                {linkText}
              </a>
            </div>
          )}
        </div>

        {/* Image Section - Always visible */}
        <div className="flex-shrink-0">
          {imageUrl ? (
            <div className="relative">
              <img 
                src={imageUrl}
                alt={title}
                className="w-48 h-32 object-cover rounded-lg"
              />
              <Button
                variant="tertiary"
                size="small"
                className="absolute top-2 right-2"
                onClick={() => {
                  setAttributes({ imageId: 0, imageUrl: '' });
                }}
              >
                🗑️
              </Button>
            </div>
          ) : (
            <MediaUploadCheck>
              <MediaUpload
                onSelect={(media) => {
                  setAttributes({ 
                    imageId: media.id, 
                    imageUrl: media.url 
                  });
                }}
                allowedTypes={['image']}
                value={imageId}
                render={({ open }) => (
                  <Button 
                    variant="secondary" 
                    onClick={open}
                    className="w-48 h-32 border-2 border-dashed border-gray-300 rounded-md flex items-center justify-center"
                  >
                    {__('Add Image', 'puroclean')}
                  </Button>
                )}
              />
            </MediaUploadCheck>
          )}
        </div>
      </div>
    </div>
  );
}
